import { StaffMember } from "@/actions/ssr/staff-management/getStaffManagementSSR";
import { createSlice } from "@reduxjs/toolkit";

export interface StaffMembersState {
  stuffMembers: StaffMember[];
  stuffMembersLoading: boolean;
  selectedStaffMember: StaffMember | null;

  // Backend pagination info
  stuffMembersPagination: {
    count: number;
    next: string | null;
    previous: string | null;
  };

  //create or edit dialog state
  staffMembersCreateEditDialogOpen: {
    open: boolean;
    openFor: "create" | "edit" | "view";
    editItemData?: Partial<StaffMember>;
  };

  staffMembersCreateEditLoading: boolean;
  staffMembersCreateEditError: string | null;
  staffMembersCreateEditFieldErrors: Record<string, string[]> | null;
  staffMembersDeleteLoading?: {
    loading: boolean;
    deleteItemId?: string;
  };

  isStaffMemberDialogOpen: {
    open: boolean;
    deleteItemId: number | undefined;
  };

  // Credentials modal state for newly created staff
  credentialsModal: {
    open: boolean;
    credentials?: {
      username: string;
      password: string;
    };
    message?: string;
  };
}

const initialState: StaffMembersState = {
  // Food types from API
  stuffMembers: [],
  stuffMembersLoading: false,
  selectedStaffMember: null,

  // Backend pagination info
  stuffMembersPagination: {
    count: 0,
    next: null,
    previous: null,
  },

  //create or edit dialog state
  staffMembersCreateEditDialogOpen: {
    open: false,
    openFor: "create",
    editItemData: undefined,
  },

  staffMembersCreateEditLoading: false,
  staffMembersCreateEditError: null,
  staffMembersCreateEditFieldErrors: null,
  staffMembersDeleteLoading: {
    loading: false,
    deleteItemId: undefined,
  },

  isStaffMemberDialogOpen: {
    open: false,
    deleteItemId: undefined,
  },

  // Credentials modal state for newly created staff
  credentialsModal: {
    open: false,
    credentials: undefined,
    message: undefined,
  },
};

export const stuffManagementSlice = createSlice({
  name: "stuffManagement",
  initialState,
  reducers: {
    setStaffMembers: (state, action) => {
      state.stuffMembers = action.payload;
    },
    setStaffMembersLoading: (state, action) => {
      state.stuffMembersLoading = action.payload;
    },
    setStaffMembersPagination: (state, action) => {
      state.stuffMembersPagination = action.payload;
    },

    //create or edit dialog state
    setStaffMembersCreateEditDialogOpen: (state, action) => {
      state.staffMembersCreateEditDialogOpen = action.payload;
    },

    clearStaffMembersCreateEditDialogOpen: (state) => {
      state.staffMembersCreateEditDialogOpen = {
        open: false,
        openFor: "create",
        editItemData: undefined,
      };
    },

    setStaffMembersCreateEditLoading: (state, action) => {
      state.staffMembersCreateEditLoading = action.payload;
    },

    setStaffMembersCreateEditError: (state, action) => {
      state.staffMembersCreateEditError = action.payload;
    },

    setStaffMembersCreateEditFieldErrors: (state, action) => {
      state.staffMembersCreateEditFieldErrors = action.payload;
    },

    setStaffMembersDeleteLoading: (state, action) => {
      state.staffMembersDeleteLoading = action.payload;
    },

    setIsStaffMemberDialogOpen: (state, action) => {
      state.isStaffMemberDialogOpen = action.payload;
    },

    setCredentialsModal: (state, action) => {
      state.credentialsModal = action.payload;
    },

    closeCredentialsModal: (state) => {
      state.credentialsModal = {
        open: false,
        credentials: undefined,
        message: undefined,
      };
    },

    setSelectedStaffMember: (state, action) => {
      state.selectedStaffMember = action.payload;
    },

    updateStaffMember: (state, action) => {
      const index = state.stuffMembers.findIndex(
        (staff) => staff.id === action.payload.id,
      );
      if (index !== -1) {
        state.stuffMembers[index] = {
          ...state.stuffMembers[index],
          ...action.payload.updates,
        };
        state.stuffMembers = [...state.stuffMembers];
      }
    },

    removeStaffMember: (state, action) => {
      const index = state.stuffMembers.findIndex(
        (staff) => staff.id === action.payload,
      );
      if (index !== -1) {
        state.stuffMembers.splice(index, 1);
        state.stuffMembers = [...state.stuffMembers];
      }
    },
  },
});

export const {
  setStaffMembers,
  setStaffMembersLoading,
  setStaffMembersPagination,
  setStaffMembersCreateEditDialogOpen,
  clearStaffMembersCreateEditDialogOpen,
  setStaffMembersCreateEditLoading,
  setStaffMembersCreateEditError,
  setStaffMembersCreateEditFieldErrors,
  setStaffMembersDeleteLoading,
  setIsStaffMemberDialogOpen,
  setCredentialsModal,
  closeCredentialsModal,
  setSelectedStaffMember,
  updateStaffMember,
  removeStaffMember,
} = stuffManagementSlice.actions;

export default stuffManagementSlice.reducer;
